/**
 * Every price on /pricing, in one place. The plan cards, the estimator and the
 * add-ons all read from here so they can't disagree.
 *
 * The Media Package isn't priced here on purpose: it's shown as coming soon
 * until it's ready to sell.
 */

export const CORE_PER_STUDENT_YEAR = 9
export const CORE_MINIMUM_YEAR = 2500
export const BRANDED_APP_YEAR = 1000
/** Charged once, in year one: setup, design, app store submission, and admin training. */
export const ONBOARDING_FEE = 1500
export const TRIAL_DAYS = 30
export const MSA_URL = 'https://media.schooljourney.com/legal/sj-master-services-agreement-v1.0.pdf'

// Billing is annual only (MSA v1.0 §5.3, invoiced annually in advance). A monthly price was shown before
// the 2026-09-28 audit but was never quoted to a school.

export type Plan = {
  id: 'trial' | 'core' | 'custom'
  name: string
  blurb: string
  /** Circled by hand on the featured plan. */
  highlight?: string
  price: string
  suffix?: string
  footnote?: string
  cta: string
  features: string[]
}

export const plans: Plan[] = [
  {
    id: 'trial',
    name: 'Free trial',
    blurb: `Free for ${TRIAL_DAYS} days with your own Blackbaud data. We’ll help you set it up so you can confidently present it to your team.`,
    price: '$0',
    cta: 'Start a free trial',
    features: [
      'Full access to everything in Core',
      'Test with members of your community, including families, faculty, and students',
      'See your news and announcements in the app',
      'If you don’t continue, we delete your trial data right away and email you a data deletion certificate'
    ]
  },
  {
    id: 'core',
    name: 'Core',
    highlight: 'Unlimited users',
    blurb: 'All the important platform features, for staff, faculty, families, and students alike.',
    price: `$${CORE_PER_STUDENT_YEAR}`,
    suffix: 'per student, per year',
    footnote: `$${CORE_MINIMUM_YEAR.toLocaleString('en-US')} minimum per year, plus a $${ONBOARDING_FEE.toLocaleString('en-US')} one-time onboarding fee`,
    cta: 'Book a demo',
    features: [
      'Secure sign-in with your Blackbaud ID',
      'School directory, calendar, and resource boards',
      'Student schedules and assignments, plus grades where your school shares them',
      'Messaging groups based on your Blackbaud groups',
      'Push notifications to families’ phones',
      'No cap on users: staff, faculty, families, and students all included',
      'One-time onboarding: setup, design, app store submission support, and up to two hours of admin training'
    ]
  },
  {
    id: 'custom',
    name: 'Custom solutions',
    blurb: 'Tailored work for schools with unique requirements or creative ideas.',
    price: 'Custom',
    cta: 'Talk to us',
    features: [
      'Everything in Core included',
      'Custom integrations and features, scoped with your school',
      'Our team works with yours from scoping to launch',
      'A clear quote before any work starts'
    ]
  }
]

/** Core with add-ons, for the estimator. Annual billing, before any custom work. */
export function estimateAnnual(students: number, brandedApp: boolean) {
  const core = Math.max(students * CORE_PER_STUDENT_YEAR, CORE_MINIMUM_YEAR)
  const branded = brandedApp ? BRANDED_APP_YEAR : 0
  return {
    core,
    branded,
    total: core + branded,
    atMinimum: students * CORE_PER_STUDENT_YEAR < CORE_MINIMUM_YEAR
  }
}

export const usd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`
